// Game type definitions and configurations

// Billiard ball colors
const BALL_COLORS = {
  1: '#FFD700',  // Yellow
  2: '#1E3A8A',  // Blue
  3: '#DC2626',  // Red
  4: '#6B21A8',  // Purple
  5: '#EA580C',  // Orange
  6: '#15803D',  // Green
  7: '#7C2D12',  // Maroon/Brown
  8: '#1a1a2e',  // Black
  9: '#FFD700',  // Yellow stripe
  10: '#1E3A8A', // Blue stripe
  11: '#DC2626', // Red stripe
  12: '#6B21A8', // Purple stripe
  13: '#EA580C', // Orange stripe
  14: '#15803D', // Green stripe
  15: '#7C2D12', // Maroon stripe
};

// Uno card colors
const UNO_COLORS = {
  red: '#DC2626',
  blue: '#2563EB',
  green: '#16A34A',
  yellow: '#FACC15',
};

// Standard playing card suits
const SUIT_COLORS = {
  hearts: '#DC2626',
  diamonds: '#DC2626',
  clubs: '#1a1a2e',
  spades: '#1a1a2e',
};

// Game type definitions
export const GAME_TYPES = {
  billiards: {
    id: 'billiards',
    name: 'Billiards',
    emoji: '🎱',
    description: 'Digital cards for pool games',
    cardType: 'ball',
    defaultCardsPerPlayer: 5,
    defaultAllowDuplicates: true,
    defaultCopies: 4,
    generateCards: (allowDuplicates = true, copies = 4) => {
      const cards = [];
      const maxCopies = allowDuplicates ? copies : 1;
      for (let copy = 0; copy < maxCopies; copy++) {
        for (let num = 1; num <= 15; num++) {
          cards.push({
            id: `ball-${num}-${copy}`,
            number: num,
            color: BALL_COLORS[num],
            isStripe: num >= 9,
            gameType: 'billiards'
          });
        }
      }
      return cards;
    }
  },
  uno: {
    id: 'uno',
    name: 'Uno',
    emoji: '🃏',
    description: 'Classic Uno card game',
    cardType: 'uno',
    defaultCardsPerPlayer: 7,
    defaultAllowDuplicates: false,
    defaultCopies: 1,
    generateCards: () => {
      const cards = [];
      const numbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
      const colors = Object.keys(UNO_COLORS);
      const actions = ['skip', 'reverse', 'draw2'];
      const wilds = ['wild', 'wild_draw4'];
      
      // Number cards (0-9) for each color
      colors.forEach(color => {
        // One 0 per color
        cards.push({
          id: `uno-${color}-0-0`,
          number: 0,
          color: UNO_COLORS[color],
          colorName: color,
          gameType: 'uno',
          type: 'number'
        });
        
        // Two of each 1-9 per color
        numbers.slice(1).forEach(num => {
          for (let i = 0; i < 2; i++) {
            cards.push({
              id: `uno-${color}-${num}-${i}`,
              number: num,
              color: UNO_COLORS[color],
              colorName: color,
              gameType: 'uno',
              type: 'number'
            });
          }
        });
        
        // Action cards (2 per color)
        actions.forEach(action => {
          for (let i = 0; i < 2; i++) {
            cards.push({
              id: `uno-${color}-${action}-${i}`,
              number: null,
              color: UNO_COLORS[color],
              colorName: color,
              action: action,
              gameType: 'uno',
              type: 'action'
            });
          }
        });
      });
      
      // Wild cards (4 of each)
      wilds.forEach(wild => {
        for (let i = 0; i < 4; i++) {
          cards.push({
            id: `uno-wild-${wild}-${i}`,
            number: null,
            color: '#4B5563',
            colorName: 'wild',
            action: wild,
            gameType: 'uno',
            type: 'wild'
          });
        }
      });
      
      return cards;
    }
  },
  playingCards: {
    id: 'playingCards',
    name: 'Playing Cards',
    emoji: '🂡',
    description: 'Standard 52-card deck',
    cardType: 'standard',
    defaultCardsPerPlayer: 7,
    defaultAllowDuplicates: false,
    defaultCopies: 1,
    generateCards: () => {
      const cards = [];
      const suits = ['hearts', 'diamonds', 'clubs', 'spades'];
      const ranks = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
      const rankValues = { 'A': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13 };
      
      suits.forEach(suit => {
        ranks.forEach(rank => {
          cards.push({
            id: `card-${suit}-${rank}`,
            suit: suit,
            rank: rank,
            number: rankValues[rank],
            color: SUIT_COLORS[suit],
            gameType: 'playingCards',
            type: 'standard'
          });
        });
      });
      
      return cards;
    }
  },
  explodingKittens: {
    id: 'explodingKittens',
    name: 'Exploding Kittens',
    emoji: '💣',
    description: 'Defuse or explode!',
    cardType: 'kitten',
    defaultCardsPerPlayer: 4,
    defaultAllowDuplicates: false,
    defaultCopies: 1,
    generateCards: () => {
      const cards = [];
      
      // Defuse cards (6 total, each player gets 1)
      for (let i = 0; i < 6; i++) {
        cards.push({
          id: `defuse-${i}`,
          name: 'Defuse',
          type: 'defuse',
          action: 'defuse',
          color: '#16a34a',
          gameType: 'explodingKittens',
          emoji: '🛡️'
        });
      }
      
      // Exploding Kitten cards (4 total)
      for (let i = 0; i < 4; i++) {
        cards.push({
          id: `exploding-${i}`,
          name: 'Exploding Kitten',
          type: 'exploding_kitten',
          color: '#dc2626',
          gameType: 'explodingKittens',
          emoji: '💣'
        });
      }
      
      // Skip cards (4 total)
      for (let i = 0; i < 4; i++) {
        cards.push({
          id: `skip-${i}`,
          name: 'Skip',
          type: 'action',
          action: 'skip',
          color: '#facc15',
          gameType: 'explodingKittens',
          emoji: '⏭️'
        });
      }
      
      // Attack cards (4 total)
      for (let i = 0; i < 4; i++) {
        cards.push({
          id: `attack-${i}`,
          name: 'Attack',
          type: 'action',
          action: 'attack',
          color: '#ea580c',
          gameType: 'explodingKittens',
          emoji: '⚔️'
        });
      }
      
      // See the Future cards (5 total)
      for (let i = 0; i < 5; i++) {
        cards.push({
          id: `future-${i}`,
          name: 'See the Future',
          type: 'action',
          action: 'see_future',
          color: '#a855f7',
          gameType: 'explodingKittens',
          emoji: '🔮'
        });
      }
      
      // Shuffle cards (4 total)
      for (let i = 0; i < 4; i++) {
        cards.push({
          id: `shuffle-${i}`,
          name: 'Shuffle',
          type: 'action',
          action: 'shuffle',
          color: '#06b6d4',
          gameType: 'explodingKittens',
          emoji: '🔀'
        });
      }
      
      // Favor cards (4 total)
      for (let i = 0; i < 4; i++) {
        cards.push({
          id: `favor-${i}`,
          name: 'Favor',
          type: 'action',
          action: 'favor',
          color: '#ec4899',
          gameType: 'explodingKittens',
          emoji: '🎁'
        });
      }
      
      // Nope cards (5 total)
      for (let i = 0; i < 5; i++) {
        cards.push({
          id: `nope-${i}`,
          name: 'Nope',
          type: 'action',
          action: 'nope',
          color: '#4b5563',
          gameType: 'explodingKittens',
          emoji: '🚫'
        });
      }
      
      // Cat cards - pairs (4 of each type, can be played in pairs to steal)
      const catNames = ['Tacocat', 'Cattermelon', 'Hairy Potato Cat', 'Rainbow-Ralphing Cat', 'Beard Cat'];
      catNames.forEach((catName, catIndex) => {
        for (let i = 0; i < 4; i++) {
          cards.push({
            id: `cat-${catIndex}-${i}`,
            name: catName,
            type: 'cat',
            number: catIndex,
            color: `hsl(${(catIndex * 60) % 360}, 70%, 50%)`,
            gameType: 'explodingKittens',
            emoji: '🐱'
          });
        }
      });
      
      return cards;
    }
  }
};

export const getGameType = (gameTypeId) => {
  return GAME_TYPES[gameTypeId] || GAME_TYPES.billiards;
};

export const getAllGameTypes = () => {
  return Object.values(GAME_TYPES);
};
